import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Ajax has no merchandise replacement operation. Add first, then remove the old
// line. Match properties and selling plan so personalised lines never get mixed.
const propertiesOf = (line) => JSON.stringify(Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const planOf = (line) => String(line.selling_plan_allocation?.selling_plan?.id || '');
const sameConfiguration = (line, original, variant) =>
  String(line.variant_id) === String(variant) && propertiesOf(line) === propertiesOf(original) && planOf(line) === planOf(original);
const matching = (cart, original, variant) => cart.items.filter((line) => sameConfiguration(line, original, variant));
const total = (lines) => lines.reduce((sum, line) => sum + line.quantity, 0);

export async function replaceCartVariant({ request, cartUrl, addUrl, changeUrl, key, variantId, sections, sectionsUrl }) {
  const before = await request(cartUrl);
  const original = before.items.find((line) => line.key === key);
  if (!original) throw new Error('The cart line changed before the variant selection.');
  if (String(original.variant_id) === String(variantId)) return { cart: before };
  const previousTargetQuantity = total(matching(before, original, variantId));
  const expectedTargetQuantity = previousTargetQuantity + original.quantity;
  let cart = before;
  try {
    const item = { id: Number(variantId), quantity: original.quantity, properties: original.properties || {} };
    if (planOf(original)) item.selling_plan = Number(planOf(original));
    await request(addUrl, { items: [item] });
    cart = await request(cartUrl);
    const oldCandidates = matching(cart, original, original.variant_id);
    const oldLine = cart.items.find((line) => line.key === key) || (oldCandidates.length === 1 ? oldCandidates[0] : null);
    if (!oldLine || oldLine.quantity !== original.quantity || total(matching(cart, original, variantId)) !== expectedTargetQuantity) {
      throw new Error('The cart changed during the variant update.');
    }
    cart = await request(changeUrl, { id: oldLine.key, quantity: 0, sections, sections_url: sectionsUrl });
    return { cart };
  } catch (error) {
    // A lost response does not prove failure: read current server state first.
    // Never retry an add blindly. If removal failed, undo only this added quantity.
    cart = await request(cartUrl);
    const oldLines = matching(cart, original, original.variant_id);
    const targetLines = matching(cart, original, variantId);
    const targetQuantity = total(targetLines);
    if (oldLines.length === 0 && targetQuantity === expectedTargetQuantity) return { cart };
    if (oldLines.length > 0 && targetQuantity === expectedTargetQuantity && targetLines.length === 1) {
      try {
        cart = await request(changeUrl, {
          id: targetLines[0].key, quantity: previousTargetQuantity, sections, sections_url: sectionsUrl,
        });
      } catch (_) {
        cart = await request(cartUrl);
      }
    }
    return { cart, error };
  }
}

const pendingUpdates = new Set();
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  pendingUpdates.add(event.promise);
  event.promise.finally(() => pendingUpdates.delete(event.promise)).catch(() => {});
});
let changingVariant = false;

class PetifoCartVariant extends HTMLElement {
  connectedCallback() { this.addEventListener('change', this.changeVariant); }
  disconnectedCallback() { this.removeEventListener('change', this.changeVariant); }

  changeVariant = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const originalValue = this.dataset.variantId;
    const variantId = select.value;
    if (changingVariant || variantId === originalValue) { select.value = originalValue; return; }
    changingVariant = true;
    const drawer = this.closest('cart-drawer-component');
    const components = [...document.querySelectorAll('cart-items-component')];
    const previousInert = components.map((component) => [component, component.inert]);
    const currentError = this.querySelector('[role="alert"]');
    currentError.hidden = true;
    drawer?.setAttribute('data-pf-variant-busy', '');
    this.setAttribute('aria-busy', 'true');
    select.disabled = true;
    const inFlight = [...pendingUpdates];
    previousInert.forEach(([component]) => { component.inert = true; });
    let deferred;
    let failed = false;
    const errorMessage = this.dataset.errorMessage;
    const originalKey = this.dataset.lineKey;
    const request = async (url, body) => {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart update failed');
      return data;
    };
    try {
      await Promise.allSettled(inFlight);
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      // Dispatch from the document so native cart components own the final render.
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [{ id: originalKey, merchandiseId: variantId }], promise: deferred.promise,
      }));
      const result = await replaceCartVariant({
        request, cartUrl: Theme.routes.cart_url + '.js', addUrl: Theme.routes.cart_add_url,
        changeUrl: Theme.routes.cart_change_url, key: originalKey, variantId,
        sections: [...new Set(components.map((c) => c.dataset.sectionId).filter(Boolean))].slice(0, 5).join(','),
        sectionsUrl: location.pathname,
      });
      failed = Boolean(result.error);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result.cart),
        detail: { items: result.cart.items, itemCount: result.cart.item_count, sections: result.cart.sections, source: 'pf-cart-variant', didError: failed },
      });
    } catch (error) {
      failed = true;
      deferred?.reject(error);
    } finally {
      previousInert.forEach(([component, inert]) => { component.inert = inert; });
      this.removeAttribute('aria-busy');
      drawer?.removeAttribute('data-pf-variant-busy');
      select.disabled = false;
      changingVariant = false;
      if (failed) {
        select.value = originalValue;
        // Keep feedback outside the morphing line so a refresh cannot erase it.
        let notice = drawer?.querySelector('.pf-cart-variant-notice');
        if (!notice && drawer) {
          notice = document.createElement('p');
          notice.className = 'pf-cart-variant-notice pf-cart-variant__error';
          notice.setAttribute('role', 'alert');
          notice.setAttribute('data-skip-node-update', '');
          drawer.querySelector('.cart-drawer__summary')?.prepend(notice);
        }
        if (notice) notice.textContent = errorMessage;
        else { currentError.textContent = errorMessage; currentError.hidden = false; }
      } else drawer?.querySelector('.pf-cart-variant-notice')?.remove();
    }
  };
}
if (!customElements.get('pf-cart-variant')) customElements.define('pf-cart-variant', PetifoCartVariant);
